import { performanceStorage } from '@/entities/performance/api/storage';
import type { PerformanceLog, PerformanceMetricType } from '@/entities/performance/domain/types';

type MetricType = 'connection_reset_retry' | 'api_response_time' | 'search_debounce_saved' | 'slow_query' | 'timeout';

type MetricData = {
  type: MetricType;
  timestamp: number;
  data: Record<string, unknown>;
};

type MetricSummary = {
  totalConnectionResetRetries: number;
  totalApiCalls: number;
  retryRate: number;
  avgResponseTime: number;
  debounceSaved: number;
  period: {
    start: number;
    end: number;
    durationMs: number;
  };
};

/**
 * 애플리케이션 성능 모니터링 클래스
 * API 응답 시간, 에러, 재시도 등의 성능 메트릭을 수집하고 분석
 */
class PerformanceMonitor {
  private metrics: MetricData[] = [];
  private readonly MAX_METRICS = 1000;
  private sessionStartTime: number = Date.now();
  private successQueryMetrics: Array<{ duration: number; responseSize: number }> = [];
  private readonly MAX_SUCCESS_QUERIES = 100;

  /**
   * 성능 메트릭 로그 기록
   * @param type - 메트릭 타입 (api_response_time, connection_reset_retry, slow_query, timeout 등)
   * @param data - 메트릭 데이터 (url, duration, status 등)
   */
  logMetric(type: MetricType, data: Record<string, unknown>) {
    const timestamp = Date.now();
    this.metrics.push({
      type,
      timestamp,
      data,
    });

    if (this.metrics.length > this.MAX_METRICS) {
      this.metrics.shift();
    }

    const log: PerformanceLog = {
      id: `${type}_${timestamp}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp,
      type: type as PerformanceMetricType,
      data,
    };
    performanceStorage.addLog(log);
  }

  /**
   * 성공한 쿼리의 성능 데이터를 메모리에 기록 (통계용)
   * @param duration - 응답 시간 (ms)
   * @param responseSize - 응답 크기 (bytes)
   */
  logSuccessQuery(duration: number, responseSize: number) {
    this.successQueryMetrics.push({ duration, responseSize });
    if (this.successQueryMetrics.length > this.MAX_SUCCESS_QUERIES) {
      this.successQueryMetrics.shift();
    }
  }

  /**
   * 메트릭 조회
   * @param type - 조회할 메트릭 타입 (선택)
   * @returns 메트릭 배열
   */
  getMetrics(type?: MetricType): MetricData[] {
    if (type) {
      return this.metrics.filter((m) => m.type === type);
    }
    return this.metrics;
  }

  /**
   * 성능 메트릭 요약 정보 조회
   * @returns 메트릭 요약 (재시도 수, API 호출 수, 평균 응답 시간 등)
   */
  getSummary(): MetricSummary {
    const retries = this.metrics.filter((m) => m.type === 'connection_reset_retry');
    const apiCalls = this.metrics.filter((m) => m.type === 'api_response_time');
    const successCalls = apiCalls.filter((m) => {
      const status = m.data.status as number | string | undefined;
      if (!status) return false;
      const statusStr = String(status);
      return statusStr.startsWith('2');
    });

    const avgResponseTime =
      successCalls.length > 0
        ? successCalls.reduce((sum, m) => sum + ((m.data.duration as number) || 0), 0) / successCalls.length
        : 0;

    const now = Date.now();

    return {
      totalConnectionResetRetries: retries.length,
      totalApiCalls: apiCalls.length,
      retryRate: apiCalls.length > 0 ? (retries.length / apiCalls.length) * 100 : 0,
      avgResponseTime: Math.round(avgResponseTime),
      debounceSaved: this.metrics.filter((m) => m.type === 'search_debounce_saved').length,
      period: {
        start: this.sessionStartTime,
        end: now,
        durationMs: now - this.sessionStartTime,
      },
    };
  }

  /**
   * 성공한 쿼리의 응답 시간 통계 조회
   * @returns 평균/최소/최대 응답 시간, 전체 개수, 평균 응답 크기
   */
  getResponseTimeStats() {
    if (this.successQueryMetrics.length === 0) {
      return { avg: 0, min: 0, max: 0, count: 0, avgSize: 0 };
    }

    const durations = this.successQueryMetrics.map((m) => m.duration);
    const sizes = this.successQueryMetrics.map((m) => m.responseSize);

    return {
      avg: Math.round(durations.reduce((a, b) => a + b, 0) / durations.length),
      min: Math.min(...durations),
      max: Math.max(...durations),
      count: durations.length,
      avgSize: Math.round(sizes.reduce((a, b) => a + b, 0) / sizes.length),
    };
  }

  /**
   * 성능 모니터링 상세 리포트 생성
   * @returns 포맷된 텍스트 리포트
   */
  getDetailedReport(): string {
    const summary = this.getSummary();
    const retries = this.getMetrics('connection_reset_retry');

    const durationMinutes = Math.round(summary.period.durationMs / 60000);

    let report = '\n=== 성능 모니터링 리포트 ===\n\n';
    report += `📊 측정 기간: ${durationMinutes}분\n`;
    report += `📈 총 API 호출: ${summary.totalApiCalls}회\n`;
    report += `⚡ 평균 응답 시간: ${summary.avgResponseTime}ms\n\n`;

    report += `🔄 Connection Reset 재시도\n`;
    report += `  - 총 재시도: ${summary.totalConnectionResetRetries}회\n`;
    report += `  - 재시도율: ${summary.retryRate.toFixed(2)}%\n\n`;

    if (retries.length > 0) {
      report += `📋 재시도 상세 내역:\n`;
      retries.forEach((retry, index) => {
        const url = (retry.data.url as string) || 'unknown';
        const errorCode = (retry.data.errorCode as string) || 'unknown';
        const time = new Date(retry.timestamp).toLocaleTimeString('ko-KR');
        report += `  ${index + 1}. [${time}] ${url.split('?')[0]} (${errorCode})\n`;
      });
      report += '\n';
    }

    report += `💾 Debounce로 절약된 요청: ${summary.debounceSaved}회\n\n`;

    report += `✅ 예상 개선 효과:\n`;
    report += `  - 에러 발생 시 자동 복구로 사용자 알림 ${summary.totalConnectionResetRetries}건 방지\n`;
    report += `  - 불필요한 API 요청 ${summary.debounceSaved}회 제거\n`;
    report += `  - 평균 응답 시간 ${summary.avgResponseTime}ms 달성\n\n`;

    report += '=========================\n';

    return report;
  }

  /**
   * 메트릭 데이터를 JSON 문자열로 내보내기
   * @returns JSON 포맷 메트릭 데이터
   */
  exportMetrics(): string {
    return JSON.stringify(
      {
        summary: this.getSummary(),
        metrics: this.metrics,
      },
      null,
      2
    );
  }

  /**
   * 모든 메트릭 데이터 초기화
   */
  clear() {
    this.metrics = [];
    this.successQueryMetrics = [];
    this.sessionStartTime = Date.now();
  }
}

export const performanceMonitor = new PerformanceMonitor();

if (typeof window !== 'undefined') {
  (window as Window & { __performanceMonitor?: PerformanceMonitor }).__performanceMonitor = performanceMonitor;
}
